import { createReducer, on } from '@ngrx/store';
import {
  AssetDetailsCommentDto,
  AssetDetailsForSidebarResponseDto,
  Comment,
  CyberAsset,
  CyberAssetsFilterOption,
  Pagination,
} from 'projects/shared/src/lib/core/interfaces';
import {
  addAssetSidebarCommentSuccess,
  clearAssetDetailsSidebar,
  fetchAssetDetailsForSidebar,
  fetchAssetDetailsForSidebarFailure,
  fetchAssetDetailsForSidebarSuccess,
  fetchAssetsFilterOptionsSuccess,
  fetchCyberAssets,
  fetchCyberAssetsFailure,
  fetchCyberAssetsSuccess,
  setAssetDetailsCommentFailed,
  setAssetDetailsCommentPending,
  setNeedUpdate,
} from './cyber-assets.actions';

export const cyberAssetsFeatureKey = 'cyberAssets';

export const emptyCyberAssetsFilterOptions: CyberAssetsFilterOption = {
  assetTypes: [],
  risks: [],
  ownershipRaci: [],
};

export type CyberAssetsState = {
  cyberAssets: CyberAsset[];
  assetsIsLoading?: boolean;
  /** True while refetching due to a filter change and previous rows are still shown. */
  assetsIsUpdating?: boolean;
  filterOptions: CyberAssetsFilterOption;
  pagination?: Pagination<CyberAsset, 'assets'>;
  assetDetails: AssetDetailsForSidebarResponseDto | null;
  assetDetailsIsLoading: boolean;
  assetDetailsLoadError: boolean;
  assetDetailsCommentPending: boolean;
  assetDetailsCommentFailed: boolean;
  assetDetailsNewComment: Comment | null;
  needUpdate: boolean;
  /** Id of the asset whose details sidebar is currently open (null when closed). */
  selectedAssetId: string | null;
};

export const initialState: CyberAssetsState = {
  cyberAssets: [],
  assetsIsLoading: true,
  assetsIsUpdating: false,
  filterOptions: emptyCyberAssetsFilterOptions,
  assetDetails: null,
  assetDetailsIsLoading: false,
  assetDetailsLoadError: false,
  assetDetailsCommentPending: false,
  assetDetailsCommentFailed: false,
  assetDetailsNewComment: null,
  needUpdate: false,
  selectedAssetId: null,
};

export const cyberAssetsReducer = createReducer(
  initialState,
  on(fetchCyberAssets, (state, { bypassLoader, filtersChanged }) => {
    return {
      ...state,
      assetsIsLoading: !bypassLoader,
      assetsIsUpdating: !!bypassLoader && !!filtersChanged,
    };
  }),

  on(fetchCyberAssetsSuccess, (state, { pagination }) => {
    return {
      ...state,
      cyberAssets: pagination.assets,
      assetsIsLoading: false,
      assetsIsUpdating: false,
      needUpdate: false,
      pagination: {
        assets: pagination.assets,
        totalCount: pagination.totalCount,
        totalPages: pagination.totalPages,
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
        shownItemsFrom: pagination.shownItemsFrom,
        shownItemsTo: pagination.shownItemsTo,
      },
    };
  }),
  on(fetchCyberAssetsFailure, (state) => {
    return { ...state, assetsIsLoading: false, assetsIsUpdating: false };
  }),
  on(fetchAssetsFilterOptionsSuccess, (state, { filterOptions }) => ({
    ...state,
    filterOptions,
  })),
  on(fetchAssetDetailsForSidebar, (state, { assetId, bypassLoader }) => ({
    ...state,
    assetDetailsIsLoading: !bypassLoader,
    assetDetailsLoadError: false,
    assetDetails: bypassLoader ? state.assetDetails : null,
    assetDetailsCommentPending: false,
    assetDetailsCommentFailed: false,
    assetDetailsNewComment: null,
    selectedAssetId: assetId ?? state.selectedAssetId,
  })),
  on(fetchAssetDetailsForSidebarSuccess, (state, { details }) => ({
    ...state,
    assetDetailsIsLoading: false,
    assetDetailsLoadError: false,
    assetDetails: details,
    assetDetailsNewComment: null,
    assetDetailsCommentFailed: false,
  })),
  on(fetchAssetDetailsForSidebarFailure, (state) => ({
    ...state,
    assetDetailsIsLoading: false,
    assetDetailsLoadError: true,
    assetDetails: null,
  })),
  on(clearAssetDetailsSidebar, (state) => ({
    ...state,
    assetDetails: null,
    assetDetailsIsLoading: false,
    assetDetailsLoadError: false,
    assetDetailsCommentPending: false,
    assetDetailsCommentFailed: false,
    assetDetailsNewComment: null,
    selectedAssetId: null,
  })),
  on(addAssetSidebarCommentSuccess, (state, { comment }) => {
    if (!state.assetDetails) {
      return {
        ...state,
        assetDetailsCommentPending: false,
        assetDetailsNewComment: comment,
      };
    }
    const comments: AssetDetailsCommentDto[] = [
      comment as AssetDetailsCommentDto,
      ...state.assetDetails.comments,
    ];
    return {
      ...state,
      assetDetails: { ...state.assetDetails, comments },
      assetDetailsNewComment: comment,
      assetDetailsCommentFailed: false,
    };
  }),
  on(setAssetDetailsCommentPending, (state, { pending }) => ({
    ...state,
    assetDetailsCommentPending: pending,
  })),
  on(setAssetDetailsCommentFailed, (state, { failed }) => ({
    ...state,
    assetDetailsCommentFailed: failed,
  })),
  on(setNeedUpdate, (state, { needUpdate }) => ({
    ...state,
    needUpdate,
  })),
);
